import { ChangeEvent, InputHTMLAttributes, KeyboardEvent, ReactNode } from "react"
import { makeStyles } from "tss-react/mui"
import { Popover, PopoverOrigin, Stack, TextField } from "@mui/material"

import { StandardButton } from "../buttons/StandardButton"

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = { vertical: "bottom", horizontal: "right" }
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = { vertical: "top", horizontal: "right" }

const useStyles = makeStyles()((theme) => ({
    popover: {
        "& .MuiPaper-root": {
            borderRadius: ".5rem",
            boxShadow: theme.shadows[5],
            backgroundColor: theme.palette.common.white,
        },
    },
    content: {
        padding: "1rem",
        width: "20rem",
        boxSizing: "border-box",
    },
    field: {
        "& .MuiOutlinedInput-root": {
            borderRadius: ".75rem",
        },
    },
    saveButton: {
        width: "100%",
    },
}))

type Props = Readonly<{
    open: boolean
    anchorEl: HTMLElement | null
    label: string
    value: string
    saveLabel: ReactNode
    onChange: (value: string) => void
    onSave: () => void
    onClose: () => void
    error?: string
    inputProps?: InputHTMLAttributes<HTMLInputElement>
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
}>

export function StandardInlineEditPopover({
    open,
    anchorEl,
    label,
    value,
    saveLabel,
    onChange,
    onSave,
    onClose,
    error,
    inputProps,
    anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
}: Props) {
    const { classes } = useStyles()
    const hasError = Boolean(error)

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault()
            onSave()
        }
    }

    return (
        <Popover
            className={classes.popover}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
        >
            <Stack gap="1rem" className={classes.content}>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label={label}
                    value={value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    inputProps={inputProps}
                    error={hasError}
                    helperText={error}
                    className={classes.field}
                />
                <StandardButton
                    color="black"
                    size="large"
                    onClick={onSave}
                    disabled={hasError}
                    className={classes.saveButton}
                >
                    {saveLabel}
                </StandardButton>
            </Stack>
        </Popover>
    )
}
